/**
 * English and Malayalam for the whole site.
 *
 * Hand-rolled rather than pulled from i18next: the app needs a lookup, an
 * interpolation and a plural rule, and all three are a few lines each against
 * a dependency that would outweigh the client's four.
 *
 * `en` is the source of truth — `ml` is a partial of it, so a key nobody has
 * translated yet renders English instead of the bare key. That is what lets
 * the two files drift without the page breaking.
 *
 * Server-sent prose (error messages, notification bodies) arrives in English
 * and stays that way, exactly as it does in the manager app.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { setFormatLocale } from '../lib/format';
import { en } from './en';
import { ml } from './ml';

export type Lang = 'en' | 'ml';

/** A phrase is either fixed, or two forms chosen by `count`. */
export type Phrase = string | { one: string; other: string };

export type Key = keyof typeof en;

/** Values interpolated into `{placeholders}`; `count` also picks the plural. */
type Vars = Record<string, string | number>;

const KEY = 'bms-lang';

const DICTS: Record<Lang, Partial<Record<Key, Phrase>>> = { en, ml };

/** The BCP-47 tag for dates and numbers — India either way. */
export const localeOf = (lang: Lang): string => (lang === 'ml' ? 'ml-IN' : 'en-IN');

/**
 * The stored choice, or null while the visitor has never picked one.
 *
 * Kept separate from the resolved language so "follow the browser" survives a
 * reload: storing the resolved 'en' would pin an English UI onto a Malayalam
 * browser forever, which is not what an untouched setting means.
 */
function storedLang(): Lang | null {
  try {
    const saved = localStorage.getItem(KEY);
    return saved === 'en' || saved === 'ml' ? saved : null;
  } catch {
    return null; // private mode: the choice just won't survive a reload
  }
}

/**
 * What the browser asks for, when nothing has been chosen: the first of its
 * languages we have. It used to pick Malayalam when Malayalam appeared anywhere
 * in the list, so a browser set to "English, then Malayalam" got Malayalam.
 */
function browserLang(): Lang {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const base = tag?.toLowerCase().split('-')[0];
    if (base === 'en' || base === 'ml') return base;
  }
  return 'en';
}

type Ctx = {
  lang: Lang;
  /** Null while following the browser — the Language menu ticks "Automatic". */
  choice: Lang | null;
  setLang: (lang: Lang | null) => void;
  t: (key: Key, vars?: Vars) => string;
  locale: string;
};

const LangContext = createContext<Ctx | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<Lang | null>(storedLang);
  const lang = choice ?? browserLang();
  const locale = localeOf(lang);

  // Set before the first paint of the new language rather than in an effect:
  // format.ts is a plain module, and an effect would let one render go out
  // with dates still in the old locale.
  setFormatLocale(locale);

  useEffect(() => {
    // Screen readers switch voice on this, and CSS can hang font stacks off it.
    document.documentElement.lang = lang;
  }, [lang]);

  // A choice made in another tab applies here too. Without this, a tab left
  // open in Malayalam kept showing Malayalam after English was picked elsewhere.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY || e.key === null) setChoice(storedLang());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setLang = useCallback((next: Lang | null) => {
    setChoice(next);
    try {
      if (next) localStorage.setItem(KEY, next);
      else localStorage.removeItem(KEY);
    } catch {
      /* private mode: the choice applies now but won't survive a reload */
    }
  }, []);

  const t = useMemo(() => translator(lang), [lang]);

  const value = useMemo<Ctx>(
    () => ({ lang, choice, setLang, t, locale }),
    [lang, choice, setLang, t, locale],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

function translator(lang: Lang) {
  const dict = DICTS[lang];
  // Malayalam plurals happen to match English's one/other split, but asking
  // Intl keeps that an observation rather than an assumption.
  const plurals = new Intl.PluralRules(localeOf(lang));

  return function t(key: Key, vars?: Vars): string {
    const phrase = dict[key] ?? en[key];
    if (phrase === undefined) {
      // A typo'd key should be visible in development and harmless in
      // production, not a blank space either way.
      if (import.meta.env.DEV) console.warn(`[i18n] missing key: ${key}`);
      return key;
    }

    let text: string;
    if (typeof phrase === 'string') {
      text = phrase;
    } else {
      const count = Number(vars?.count ?? 0);
      text = plurals.select(count) === 'one' ? phrase.one : phrase.other;
    }

    if (!vars) return text;
    return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
      const value = vars[name];
      if (value === undefined) return whole;
      return typeof value === 'number' ? value.toLocaleString(localeOf(lang)) : value;
    });
  };
}

export function useT() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useT must be used inside <LangProvider>');
  return ctx;
}
